import React from "react";
import "./Testimonials.css";
import { FaStar } from "react-icons/fa";
import {
  testimonials as defaultTestimonials,
  logos as defaultLogos,
} from "../../data/testimonials";

function Stars({ value = 5, size = "md" }) {
  const full = Math.max(0, Math.min(5, Math.round(value)));
  return (
    <span
      className={`tx3-stars tx3-stars--${size}`}
      aria-label={`${full} out of 5 stars`}
    >
      {Array.from({ length: 5 }).map((_, i) => (
        <FaStar key={i} className={`tx3-star ${i < full ? "is-on" : ""}`} />
      ))}
    </span>
  );
}

export default function Testimonials({ items, clientLogos }) {
  const list = Array.isArray(items) ? items : defaultTestimonials;
  const logos = Array.isArray(clientLogos) ? clientLogos : defaultLogos;

  if (!list?.length) return null;
  const [featured, ...rest] = list;

  // Next up to 3 as main cards; remainder -> strip
  const mains = rest.slice(0, 3);
  const tail = rest.slice(3);

  return (
    <section className="tx3" aria-label="Customer testimonials">
      <div className="wrap">
        {/* Header */}
        <header className="tx3-head">
          <span className="tx3-eyebrow">What clients say</span>
          <h2 className="tx3-title">Proof, not promises.</h2>
          <p className="tx3-sub">
            Real outcomes across PPC, SEO, Web, and Security—clear
            communication, tight timelines, measurable lift.
          </p>
        </header>

        <div className="tx3-layout">
          {/* Full-width featured card */}
          <article className="tx3-feature" aria-label="Featured testimonial">
            <div className="tx3-fx" aria-hidden />
            <div className="tx3-feature__inner">
              <div className="tx3-feature__top">
                <div className="tx3-avatar">
                  {featured.photo ? (
                    <img src={featured.photo} alt={featured.name} />
                  ) : (
                    <div className="tx3-avatar__fallback" aria-hidden>
                      {featured.name?.[0] || "★"}
                    </div>
                  )}
                </div>

                <div className="tx3-id">
                  <strong className="tx3-name">{featured.name}</strong>
                  <small className="tx3-role">
                    {featured.role} • {featured.company}
                  </small>
                  <Stars value={featured.rating ?? 5} size="lg" />
                </div>

                {featured.logo && (
                  <div className="tx3-brand">
                    <img src={featured.logo} alt={`${featured.company} logo`} />
                  </div>
                )}
              </div>

              <blockquote className="tx3-quote">
                <span className="tx3-qmark" aria-hidden>
                  “
                </span>
                <p>{featured.quote}</p>
              </blockquote>
            </div>
          </article>

          {/* 3-card grid underneath */}
          {mains.length > 0 && (
            <ul className="tx3-row" role="list">
              {mains.map((t, i) => (
                <li
                  key={t.id || `${t.name}-${i}`}
                  className="tx3-card tx3-card--row"
                >
                  <div className="tx3-card__head">
                    <div className="tx3-mini">
                      {t.photo ? (
                        <img src={t.photo} alt={t.name} />
                      ) : (
                        <div className="tx3-mini__fallback" aria-hidden>
                          {t.name?.[0] || "★"}
                        </div>
                      )}
                    </div>
                    <div className="tx3-card__id">
                      <strong>{t.name}</strong>
                      <small>
                        {t.role} • {t.company}
                      </small>
                    </div>
                    <Stars value={t.rating ?? 5} />
                  </div>
                  <p className="tx3-card__quote">“{t.quote}”</p>
                  {t.logo && (
                    <div className="tx3-card__logo" aria-hidden>
                      <img src={t.logo} alt="" />
                    </div>
                  )}
                </li>
              ))}
            </ul>
          )}

          {/* Optional slim strip for any remaining quotes */}
          {tail.length > 0 && (
            <ul className="tx3-strip" role="list" aria-label="More feedback">
              {tail.map((t, i) => (
                <li
                  key={t.id || `${t.name}-tail-${i}`}
                  className="tx3-chip"
                >
                  <span className="tx3-chip__name">{t.name}</span>
                  <span className="tx3-chip__dot">·</span>
                  <span className="tx3-chip__text">{t.quote}</span>
                </li>
              ))}
            </ul>
          )}

          {/* Optional client logos */}
          {/* {logos?.length > 0 && (
            <div className="tx3-logos" aria-label="Selected clients">
              <ul className="tx3-logos__list" role="list">
                {logos.map((src, i) => (
                  <li key={i}>
                    <img src={src} alt="Client logo" />
                  </li>
                ))}
              </ul>
            </div>
          )} */}
        </div>
      </div>
    </section>
  );
}
